import { Environment } from "@/utils/environment";

/**
 * Payment gateways and Passport domains must not reset marketing attribution (Globeful + PASGF-1624).
 * Extend when new PSPs are added (see payment URLs spreadsheet in Jira).
 */
export const AMPLITUDE_EXCLUDE_REFERRERS: Array<string | RegExp> = [
  /mypassporthub\.com$/,
  /mypassportshub\.com$/,
  /quickpassports\.org$/,
  /us-passports\.org$/,
];

export const AMPLITUDE_EVENT_SCHEMA_VERSION = "1.0.0";

/** Globeful mandatory event properties (rules 1–3). Applied in amplitudeTrackEvent only. */
export type AmplitudeMandatoryEventProperties = {
  domain: string;
  live_mode: boolean;
  version: string;
};

export const getAmplitudeMandatoryEventProperties = (): AmplitudeMandatoryEventProperties => {
  const domain =
    typeof window !== "undefined" && window.location?.hostname ? window.location.hostname : "";

  return {
    domain,
    live_mode: Environment.isProd(),
    version: AMPLITUDE_EVENT_SCHEMA_VERSION,
  };
};

/**
 * Closed Amplitude `project` dictionary (PASGF-2176 / PASGF-2353).
 * All values are lowercase. Any other emitted value is a defect.
 */
export const AMPLITUDE_PROJECT = "passports";
export const AMPLITUDE_PROJECT_EASEGOV = "easegov";
export const AMPLITUDE_PROJECT_MYPASSPORTHUB = "mypassporthub";
/** Canonical MyUsaPassport project id (singular — not `myusapassports`). */
export const AMPLITUDE_PROJECT_MYUSAPASSPORT = "myusapassport";

/** @deprecated Use AMPLITUDE_PROJECT_MYUSAPASSPORT. Kept as alias for older imports. */
export const AMPLITUDE_PROJECT_MYUSAPASSPORTS = AMPLITUDE_PROJECT_MYUSAPASSPORT;

export const AMPLITUDE_CANONICAL_PROJECTS = [
  AMPLITUDE_PROJECT,
  AMPLITUDE_PROJECT_EASEGOV,
  AMPLITUDE_PROJECT_MYPASSPORTHUB,
  AMPLITUDE_PROJECT_MYUSAPASSPORT,
] as const;

export type AmplitudeCanonicalProject = (typeof AMPLITUDE_CANONICAL_PROJECTS)[number];

const AMPLITUDE_PROJECT_STORAGE_KEY = "amp_project";

/** Known non-canonical aliases → dictionary value (casing / legacy typo). */
const AMPLITUDE_PROJECT_ALIASES: Record<string, AmplitudeCanonicalProject> = {
  myusapassports: AMPLITUDE_PROJECT_MYUSAPASSPORT,
};

const endsWithDomain = (hostname: string, domain: string): boolean =>
  hostname === domain || hostname.endsWith(`.${domain}`);

/** Certified reporting sites — always emit their own project (ignore external amp_project). */
const isEasegovHost = (hostname: string): boolean => endsWithDomain(hostname, "easegov.com");

const isMyPassportHubHost = (hostname: string): boolean =>
  endsWithDomain(hostname, "mypassporthub.com");

const isMyUsaPassportHost = (hostname: string): boolean =>
  endsWithDomain(hostname, "myusapassport.com");

/**
 * Shared payment gateway hosts (Template 5 / MPH funnel).
 * Only these hosts may accept amp_project / sessionStorage overrides from the origin site.
 */
export const isSharedPaymentHost = (hostname: string): boolean =>
  endsWithDomain(hostname.toLowerCase(), "us-passports.org");

/**
 * Lowercases and maps aliases into the closed dictionary.
 * Unknown values become `passports` (never raw domain slugs like `fastfillings` / `us-passports`).
 */
export const normalizeAmplitudeProject = (
  value: string | null | undefined
): AmplitudeCanonicalProject => {
  if (!value) return AMPLITUDE_PROJECT;

  const normalized = value.trim().toLowerCase();
  const aliased = AMPLITUDE_PROJECT_ALIASES[normalized] ?? normalized;

  return (AMPLITUDE_CANONICAL_PROJECTS as readonly string[]).includes(aliased)
    ? (aliased as AmplitudeCanonicalProject)
    : AMPLITUDE_PROJECT;
};

/** Maps hostname → Amplitude project. Includes prod and test env subdomains. */
export const resolveAmplitudeProjectFromHostname = (
  hostname: string
): AmplitudeCanonicalProject => {
  const host = hostname.toLowerCase();

  if (isEasegovHost(host)) return AMPLITUDE_PROJECT_EASEGOV;
  if (isMyPassportHubHost(host)) return AMPLITUDE_PROJECT_MYPASSPORTHUB;
  if (isMyUsaPassportHost(host)) return AMPLITUDE_PROJECT_MYUSAPASSPORT;

  return AMPLITUDE_PROJECT;
};

let activeAmplitudeProject: string | null = null;

/**
 * Resolves Amplitude `project` (PASGF-2176 / PASGF-2353):
 * 1. Certified host → always hostname mapping (blocks amp_project contamination).
 * 2. Shared payment host → amp_project URL / sessionStorage (canonicalized), else passports.
 * 3. Everything else → passports.
 */
export const resolveAmplitudeProject = (): string => {
  if (activeAmplitudeProject) return activeAmplitudeProject;

  if (typeof window !== "undefined") {
    const hostname = (window.location?.hostname ?? "").toLowerCase();
    const hostnameProject = resolveAmplitudeProjectFromHostname(hostname);

    if (!isSharedPaymentHost(hostname) && hostnameProject !== AMPLITUDE_PROJECT) {
      activeAmplitudeProject = hostnameProject;
      return activeAmplitudeProject;
    }

    if (isSharedPaymentHost(hostname)) {
      const urlParams = new URLSearchParams(window.location.search);
      const ampProjectFromUrl = urlParams.get(AMPLITUDE_PROJECT_STORAGE_KEY);

      if (ampProjectFromUrl) {
        const canonicalFromUrl = normalizeAmplitudeProject(ampProjectFromUrl);
        sessionStorage.setItem(AMPLITUDE_PROJECT_STORAGE_KEY, canonicalFromUrl);
        activeAmplitudeProject = canonicalFromUrl;
        return activeAmplitudeProject;
      }

      const stored = sessionStorage.getItem(AMPLITUDE_PROJECT_STORAGE_KEY);
      activeAmplitudeProject = stored ? normalizeAmplitudeProject(stored) : AMPLITUDE_PROJECT;
      return activeAmplitudeProject;
    }

    activeAmplitudeProject = AMPLITUDE_PROJECT;
  } else {
    activeAmplitudeProject = AMPLITUDE_PROJECT;
  }

  return activeAmplitudeProject;
};

export const getAmplitudeProject = (): string =>
  activeAmplitudeProject ?? resolveAmplitudeProject();

/** BE request header names. */
export const getAmplitudeApiHeaderNames = () => ({
  sessionId: "x-amplitudeSessionId",
  deviceId: "x-amplitudeDeviceId",
  project: "x-amplitudeProject",
});

/** init options aligned with Globeful Browser SDK 2 mandatory attribution config. */
export const AMPLITUDE_INIT_OPTIONS = {
  serverZone: "EU" as const,
  fetchRemoteConfig: true,
  autocapture: {
    attribution: {
      excludeReferrers: AMPLITUDE_EXCLUDE_REFERRERS,
      resetSessionOnNewCampaign: false,
    },
    pageUrlEnrichment: true,
    pageViews: false,
    sessions: true,
    formInteractions: false,
    fileDownloads: false,
    elementInteractions: false,
    webVitals: false,
  },
};
